import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  inject,
} from "@angular/core";
import { UploadComponentService } from "./data-access/upload-component-service";
import { MatIconModule } from "@angular/material/icon";
import { CommonModule } from "@angular/common";
import { MatTooltipModule } from "@angular/material/tooltip";
import { FileDropDirective } from "./directive/file-drop.directive";
import { takeWhile } from "rxjs";
import { UploadProcessResponse } from "@shared-interfaces/shared-upload";
import { FileData } from "@shared-interfaces/shared-file";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { MatCardModule } from "@angular/material/card";
import { MatProgressBarModule } from "@angular/material/progress-bar";

@Component({
  selector: "shared-upload-2",
  standalone: true,
  imports: [
    MatIconModule,
    CommonModule,
    MatTooltipModule,
    FileDropDirective,
    MatProgressSpinnerModule,
    MatCardModule,
    MatProgressBarModule,
  ],
  templateUrl: "./upload.component.html",
  styleUrl: "./upload.component.scss",
})
export class UploadComponent {
  // Used for unique identification in frontend
  @Input() identifier: string | undefined = undefined;
  // Defines the storage location of the running process
  @Input() workflowId: string | undefined = undefined;

  @Input() fileLimit: number = 1;
  @Input() fileFormats: string[] = [
    "application/pdf",
    "text/plain",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/msword",
  ];
  @Input() fileList: FileData[] | undefined = [];
  @Output() fileProcessEvent: EventEmitter<UploadProcessResponse> =
    new EventEmitter<UploadProcessResponse>();
  @Output() deleteFileEvent: EventEmitter<FileData> =
    new EventEmitter<FileData>();
  fileExtension: any;

  constructor() {
    this.uploadService.processes
      .pipe(takeUntilDestroyed())
      .subscribe((uploadData: UploadProcessResponse) => {
        if (
          uploadData.process.details.frontendIdentifier === this.workflowId
        ) {
          this.removeTempFile(uploadData);
          this.fileProcessEvent.emit(uploadData);
        }
      });
  }

  tempFiles: FileData[] = [];
  uploadService = inject(UploadComponentService);

  resetChangeDetection(): void {
    let input: any = document.getElementById("file");
    input["value"] = null;
  }

  async onFileChange(fileList: any | Event) {
    if (fileList["files"]) {
      for (const key of Object.keys(fileList["files"])) {
        await this.uploadService.uploadFile$.next({
          file: fileList["files"][key],
          workflowId: this.workflowId,
        });
        this.addDummyFile(fileList["files"][key]);
        await new Promise((resolve) => setTimeout(resolve, 500)); // Pause for 1 second
      }
    } else {
      for (const key of Object.keys(fileList)) {
        await this.uploadService.uploadFile$.next({
          file: fileList[key],
          workflowId: this.workflowId,
        });
        this.addDummyFile(fileList[key]);
        await new Promise((resolve) => setTimeout(resolve, 500)); // Pause for 1 second
      }
    }
  }

  private addDummyFile(file: File) {
    this.fileExtension = file.name.split(".").pop();
    this.tempFiles.push({
      displayName: file.name.split(".")[0],
      fileExtension: this.fileExtension.toLowerCase(),
      status: "running",
      storedName: undefined,
      uploadedBy: undefined,
      uploadedAt: undefined,
    });
  }

  private removeTempFile(uploadData: UploadProcessResponse) {
    this.tempFiles = this.tempFiles.filter(
      (element: FileData) => element.displayName !== uploadData.process.name
    );
  }
  deleteFile(file: FileData) {
    this.deleteFileEvent.emit(file);
    this.uploadService.deleteFile$.next({workflowId: this.workflowId!, ...file});
    this.tempFiles = this.tempFiles.filter(
      (element: FileData) => !(element.displayName == file.displayName)
    );
  }

  delay(ms: number) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
